import { z } from "zod";

const INVALID_URL = "Enter a valid http or https URL";

/** True when value is an absolute http or https URL. */
export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Non-blank URL strings from a single answer or a list. */
export function urlAnswerValues(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Validation message, or null when the answer is acceptable. */
export function urlAnswerError(
  value: unknown,
  required: boolean,
  allowMultiple: boolean,
): string | null {
  if (value != null && typeof value !== "string" && !Array.isArray(value)) {
    return INVALID_URL;
  }
  if (
    !allowMultiple &&
    Array.isArray(value) &&
    urlAnswerValues(value).length > 1
  ) {
    return "Only one URL is allowed";
  }
  const urls = urlAnswerValues(value);
  if (urls.length === 0) {
    return required
      ? allowMultiple
        ? "Enter at least one URL"
        : "This field is required"
      : null;
  }
  if (urls.some((url) => !isHttpUrl(url))) return INVALID_URL;
  return null;
}

/** Zod schema for a URL question. Single answers are strings; multiple are string arrays. */
export function urlAnswerSchema(
  required: boolean,
  allowMultiple: boolean,
): z.ZodTypeAny {
  return z.any().superRefine((value, ctx) => {
    const message = urlAnswerError(value, required, allowMultiple);
    if (message) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    }
  });
}
